import { evoPathwayIcon } from "@/icons";
import { cn } from "@/lib/utils";
import { useTranslation } from "@repo/shared/i18n";
import { X } from "lucide-react";

/**
 * How the node reacts to a press.
 *
 * "editable" — label opens the step, the × half removes it (builder chain).
 * "readonly" — label opens the step, no × (player page, sheet header).
 * "selectable" — label selects the step; `selected` draws the ring, the rest
 *   fade (inside the chain sheet).
 */
export type EvoNodeVariant = "editable" | "readonly" | "selectable";

export interface EvoNodeProps {
  label: string;
  /** Text after the label behind a dot, e.g. the chosen path "B". */
  detail?: string;
  variant?: EvoNodeVariant;
  /** Draws the branch marker before the label. */
  isPathway?: boolean;
  expired?: boolean;
  /** "selectable" only. */
  selected?: boolean;
  onOpen?: () => void;
  /** "editable" only; the × half is omitted without it. */
  onRemove?: () => void;
  /** Clip a long name to the available width instead of overflowing it. */
  truncate?: boolean;
  className?: string;
}

/**
 * One step of an evolution chain as a chip. Two hit areas in one rounded
 * surface: the label and, on editable chains, a × behind a hairline divider.
 * Gold on green, or muted grey once the evolution has expired.
 */
export const EvoNode = ({
  label,
  detail,
  variant = "readonly",
  isPathway = false,
  expired = false,
  selected = false,
  onOpen,
  onRemove,
  truncate = false,
  className,
}: EvoNodeProps) => {
  const { t } = useTranslation("evolutions");
  const selectable = variant === "selectable";
  const showRemove = variant === "editable" && onRemove;

  const labelClasses =
    "inline-flex min-w-0 items-center gap-1 px-2 leading-4 whitespace-nowrap";
  const content = (
    <>
      {isPathway && (
        <div className="size-3.5 shrink-0 text-evolution-secondary">
          {evoPathwayIcon}
        </div>
      )}
      <span className={cn(truncate && "truncate")}>{label}</span>
      {detail && (
        <>
          <span
            className="size-1 shrink-0 rounded-full bg-current/70"
            aria-hidden
          />
          <span>{detail}</span>
        </>
      )}
    </>
  );

  return (
    <div
      data-evo-node
      className={cn(
        "inline-flex h-6 items-stretch overflow-hidden rounded-sm text-sm transition-[opacity,box-shadow] duration-100 touch-device:h-7",
        truncate ? "min-w-0 max-w-full" : "shrink-0",
        expired
          ? "bg-disabled text-dark-tertiary"
          : "bg-evolution text-evolution-secondary",
        selectable && !selected && "opacity-70 hover:opacity-100",
        selectable && selected && "ring-1 ring-evolution-secondary",
        className,
      )}
    >
      {/* A pressable step is a button; a plain one is inert text, so clicks
          and the cursor fall through to whatever card the chain sits on. */}
      {onOpen ? (
        <button
          type="button"
          onClick={(e) => {
            // The chip often sits on a row that is itself a link or a
            // button; opening a step must not also trigger that.
            e.preventDefault();
            e.stopPropagation();
            onOpen();
          }}
          aria-pressed={selectable ? selected : undefined}
          className={cn(
            labelClasses,
            "cursor-pointer outline-none hover:bg-white/5 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-white/40",
          )}
        >
          {content}
        </button>
      ) : (
        <span className={labelClasses}>{content}</span>
      )}
      {showRemove && (
        <>
          <div className="w-px bg-current/30" aria-hidden />
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onRemove();
            }}
            aria-label={t("chain.remove", { name: label })}
            className="grid w-6 shrink-0 cursor-pointer place-items-center text-current/60 outline-none hover:bg-white/5 hover:text-current focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-white/40"
          >
            <X className="size-3" />
          </button>
        </>
      )}
    </div>
  );
};
